import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    signal,
    SimpleChanges,
} from '@angular/core';
import { TipService } from '@interacta-shared/feature-tip';
import { Index, isDefined, isNumberIndex } from '@interacta-shared/util';
import { IAttachment } from '@interacta/util-post';
import {
    AIContextType,
    AIMessage,
    AIMessageType,
    AIRole,
    MAIN_THREAD,
} from '@modules/ai/models/ai.model';
import { AIActions } from '@modules/ai/store/ai.actions';
import {
    selectActiveHistory,
    selectCachedAttachment,
} from '@modules/ai/store/ai.selectors';

import { FeedbackChip } from '@interacta-shared/ui-ai';
import { formatMessage } from '@modules/ai-shared/helpers/ai-shared.utils';
import { Store } from '@ngrx/store';
import {
    BehaviorSubject,
    concat,
    concatMap,
    filter,
    from,
    ignoreElements,
    Observable,
    of,
    Subject,
    takeUntil,
    timer,
} from 'rxjs';

@Component({
    selector: 'interacta-ai-message',
    templateUrl: './ai-message.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AIMessageComponent implements OnInit, OnChanges, OnDestroy {
    private store = inject(Store);
    private tipService = inject(TipService);

    @Input({ required: true }) item!: AIMessage;
    @Input({ required: true }) messageKey!: string;
    @Input({ required: true }) theme!: 'light' | 'dark';
    @Input({ required: true }) contextType!: AIContextType;
    @Input() threadId: Index = MAIN_THREAD;
    @Input() last = false;

    @Output() scrollToBottom = new EventEmitter<void>();
    @Output() retry = new EventEmitter<void>();
    @Output() openAttachmentPreview = new EventEmitter<IAttachment>();
    @Output() goToThread = new EventEmitter<Index>();

    displayedMessage = signal('');
    readonly typing = signal(false);
    messagesCount$ = new BehaviorSubject<number>(0);
    hasErrors$ = new BehaviorSubject<boolean | undefined>(undefined);
    attachmentDetail$ = new Observable<IAttachment | null>();
    destroy$ = new Subject<void>();

    readonly AIRole = AIRole;
    readonly AIMessageType = AIMessageType;
    readonly AIContextType = AIContextType;
    readonly MAIN_THREAD = MAIN_THREAD;

    ngOnInit(): void {
        if (
            this.item.attachmentId &&
            isNumberIndex(this.item.attachmentId) &&
            this.item.messageType === AIMessageType.ATTACHMENT
        ) {
            this.attachmentDetail$ = this.store.select(
                selectCachedAttachment(this.item.attachmentId),
            );
        }

        if (this.item.role === AIRole.MODEL && !this.item.alreadyAnimated) {
            const stringFormatted = formatMessage(this.item.message);
            this.typing.set(true);
            from(stringFormatted.split(/(\s+)/g))
                .pipe(
                    concatMap((word) =>
                        concat(of(word), timer(30).pipe(ignoreElements())),
                    ),
                    takeUntil(this.destroy$),
                )
                .subscribe({
                    next: (word) =>
                        this.displayedMessage.update((value) => value + word),
                    complete: () => {
                        this.typing.set(false);
                        this.store.dispatch(
                            AIActions.setAlreadyAnimated({
                                clientId: this.messageKey,
                                contextType: this.contextType,
                                threadId: this.threadId,
                            }),
                        );
                        this.scrollToBottom.emit();
                    },
                });
        } else {
            this.displayedMessage.set(formatMessage(this.item.message));
            this.scrollToBottom.emit();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes?.item &&
            this.item.attachmentId &&
            isNumberIndex(this.item.attachmentId) &&
            this.item.messageType === AIMessageType.ATTACHMENT &&
            (changes.item.isFirstChange() ||
                this.item.attachmentId !==
                    changes.item.previousValue.attachmentId)
        ) {
            this.store.dispatch(
                AIActions.getAttachmentDetail({ id: this.item.attachmentId }),
            );

            this.store
                .select(selectActiveHistory(this.item.attachmentId))
                .pipe(filter(isDefined), takeUntil(this.destroy$))
                .subscribe((value) => {
                    this.messagesCount$.next(
                        Object.keys(value.history).length - 1,
                    );
                    this.hasErrors$.next(value.hasErrors);
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    setLike(actualLikeValue: boolean | undefined, likeWanted: boolean): void {
        const like = actualLikeValue === likeWanted ? undefined : likeWanted;

        this.store.dispatch(
            AIActions.sendFeedback({
                key: this.messageKey,
                messageId: this.item.id,
                like,
                contextType: this.contextType,
                threadId: this.threadId,
            }),
        );
    }

    sendFeedbackDetail($event: {
        chips: FeedbackChip[];
        moreDetails: string | null;
    }): void {
        if (this.item.like === undefined) return;

        this.store.dispatch(
            AIActions.sendFeedbackDetail({
                key: this.messageKey,
                messageId: this.item.id,
                like: this.item.like,
                feedbacks: $event.chips?.map((c) => c.id),
                text: $event.moreDetails ?? undefined,
                contextType: this.contextType,
                threadId: this.threadId,
            }),
        );
    }

    copyText(): void {
        navigator.clipboard.writeText(this.item.message);
        this.tipService.success('AI.COPY_SUCCESS');
    }
}
